import { makeAutoObservable } from "mobx";
import { INCENTIVE_TYPES, INCENTIVE_VIRTUAL_CURRENCIES, ISwitchToExchangeSetting } from "../models/User";
import { IChangeable } from "./types";
import { IReturnReason } from "../models/ReturnReasonTypes";

interface ISwitchToExchangeSettingChanges extends Partial<ISwitchToExchangeSetting> {
};

class SwitchToExchangeStore implements IChangeable<ISwitchToExchangeSettingChanges>{
    private _model: ISwitchToExchangeSetting = {} as ISwitchToExchangeSetting;
    private _changes: ISwitchToExchangeSettingChanges = {};

    constructor(switch_to_exchange_setting: ISwitchToExchangeSetting, currencyCode?: string) {
        makeAutoObservable(this, {}, { autoBind: true });
        this.setModel(switch_to_exchange_setting, currencyCode);
    }

    private setModel(model: ISwitchToExchangeSetting, currencyCode?: string) {
        if (model) {
            this._model = model;
            this._model.incentive_currency = currencyCode;
        }
        this.discardChanges();
    }

    public hasChanges() {
        return !!Object.keys(this._changes).length;
    }

    public getUpdatedModel(): ISwitchToExchangeSetting {
        return { ...this._model, ...this._changes };
    }

    public discardChanges(): void {
        this._changes = {};
    }

    public get isActive(): boolean {
        return this._changes.is_active ?? this._model.is_active;
    }

    public get incentiveType(): INCENTIVE_TYPES|null {
        return this._changes.incentive_type ?? this._model?.incentive_type;
    }

    public get incentiveAmount(): number|undefined {
        const amount = Number(this._changes.incentive_amount ?? this._model?.incentive_amount);
        return !isNaN(amount) ? +amount.toFixed(0) : undefined;
    }

    public get incentiveCurrency(): string|undefined {
        return this._model?.incentive_currency;
    }

    public get incentiveVirtualCurrency(): INCENTIVE_VIRTUAL_CURRENCIES {
        return this._changes.incentive_virtual_currency ?? this._model?.incentive_virtual_currency;
    }

    public get eligibleReturnReasons(): IReturnReason[] {
        return this._model?._additional_info?.eligible_return_reasons ?? [];
    }

    private _clearChangesIfEqual(key: keyof ISwitchToExchangeSetting): void {
        if (this._changes[key] === this._model?.[key]) {
            delete this._changes[key];
        }
    }

    public setIsActive(value: boolean) {
        this._changes.is_active = value;
        if (value && !this.incentiveType) {
            this.setIncentiveType(INCENTIVE_TYPES.FIXED_PRICE);
        }
        this._clearChangesIfEqual('is_active');
    }

    public setIncentiveType(v: INCENTIVE_TYPES) {
        this._changes.incentive_type = v;
        this._clearChangesIfEqual('incentive_type');
    }

    public setIncentiveAmount(v: number|undefined) {
        this._changes.incentive_amount = v;
        if (+Number(this._changes.incentive_amount).toFixed(0) === +Number(this._model?.incentive_amount).toFixed(0)) {
            delete this._changes.incentive_amount;
        }
    }

    public setIncentiveCurrency(v: string) {
        this._changes.incentive_currency = v;
        this._clearChangesIfEqual('incentive_currency');
    }

    public setIncentiveVirtualCurrency(v: INCENTIVE_VIRTUAL_CURRENCIES) {
        this._changes.incentive_virtual_currency = v;
        this._clearChangesIfEqual('incentive_virtual_currency');
    }

}

export default SwitchToExchangeStore;
